/**
 * Quies's feature page on `@core/proof`, one spec per home language. The sections derive from `../PROPOSITION.md`; the
 * words are `copy.json`, written natively for each language by the Haiku seat (never translated from another).
 * The page is each language's home (`routes/_layout.($lang)._index.tsx`).
 */
import type {DemoSpec, ProofSpec} from '@core/proof/spec'
import {HOME_LANGS, type HomeLang} from '@product/quies/lib/homeLangs'
import copyByLang from './copy.json'

export const SESSION_PATH = '/session'

export type QuiesCopy = (typeof copyByLang)['en']
export const quiesCopy = (lang: HomeLang): QuiesCopy => copyByLang[lang]

/** The `steps` visual of the first section names the product's own screens by this. */
export const WALKTHROUGH_REF = 'walkthrough'

function demosOf(copy: QuiesCopy): DemoSpec[] {
    return [
        {kind: 'steps', title: copy.written.title, body: copy.written.body},
        {kind: 'listen', title: copy.listen.title, body: copy.listen.body},
        {kind: 'sessions', title: copy.tongues.title, body: copy.tongues.body},
        {kind: 'note', title: copy.remembers.title, body: copy.remembers.body},
        {kind: 'links', title: copy.methods.title, body: copy.methods.body},
    ]
}

function specOf(lang: HomeLang): ProofSpec {
    const copy = quiesCopy(lang)
    return {
        product: 'quies',
        domain: 'quies.ai',
        searchQuery: '',
        catalogueSlugs: [],
        chips: [],
        demos: demosOf(copy),
        copy: {
            hero: {title: copy.hero.title, line: copy.hero.line, cta: {label: copy.hero.cta, to: SESSION_PATH}},
            mailHeadline: copy.mailHeadline,
            demosHeading: null,
            shownOn: null,
            chipsHeading: null,
            picksHeading: '',
            closing: {title: copy.closing.title, body: copy.closing.body, link: copy.closing.cta, to: SESSION_PATH},
            tags: {pairFrom: '', pairWithOn: '', before: '', after: '', packs: '', pictures: '', listen: copy.tags.listen},
            alts: {sample: '', shown: '', pairWithOn: '', editBefore: '', editAfter: ''},
        },
    }
}

export const quiesProofSpecs = Object.fromEntries(HOME_LANGS.map((lang) => [lang, specOf(lang)])) as Record<HomeLang, ProofSpec>
